// src/components/doctor/VitalsTrendChart.jsx
//
// A small inline-SVG line chart for the Vitals tab (no chart library — same
// approach as RiskTrendCard). It draws only shapes: dates, values and labels
// live in HTML around it, so text keeps its size on a phone and stays
// selectable. The SVG is an image (role="img" + a spoken summary); the
// "All readings" table on the same tab is the accessible alternative.
//
// series: [{ key, color, dashed?, points: [{t, value, flagged?, label}] }]
//   Points are oldest → newest. One point renders as a single dot.
// band:   { from } shades the area from `from` up to the top (the BP "high" zone)
// refs:   [{ value }] faint dashed reference lines

import React, { useMemo } from 'react';
import { chartScale } from '../../utils/vitalsTrend';

const W = 420;
const PAD_X = 14;
const TOP = 10;

export default function VitalsTrendChart({ series, band, refs = [], ariaLabel, minSpan = 1, height = 120 }) {
  const H = height;
  const BOTTOM = H - 10;
  const geo = useMemo(() => {
    const all = series.flatMap(s => s.points);
    const values = all.map(p => p.value);
    const include = [...(band ? [band.from] : []), ...refs.map(r => r.value)];
    const scale = chartScale(values, { include, minSpan });
    const ts = all.map(p => p.t);
    const t0 = Math.min(...ts);
    const t1 = Math.max(...ts);
    const x = t => (t1 === t0 ? W / 2 : PAD_X + ((t - t0) / (t1 - t0)) * (W - PAD_X * 2));
    const y = v => BOTTOM - scale.at(v) * (BOTTOM - TOP);
    return { x, y, scale };
  }, [series, band, refs, minSpan, BOTTOM]);

  return (
    <svg className="prec-chart__svg" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={ariaLabel} focusable="false">
      {band && (
        <rect x="0" width={W} y={TOP - 4} height={Math.max(0, geo.y(band.from) - (TOP - 4))}
          fill="var(--red-bg)" stroke="var(--red)" strokeOpacity=".35" strokeDasharray="3 4" />
      )}
      {refs.map(r => (
        <line key={r.value} x1="0" x2={W} y1={geo.y(r.value)} y2={geo.y(r.value)}
          stroke="var(--border-strong)" strokeDasharray="3 4" />
      ))}
      {series.map(s => (
        <g key={s.key} aria-hidden="true">
          {s.points.length > 1 && (
            <polyline fill="none" stroke={s.color} strokeWidth="2.4" strokeLinejoin="round" strokeLinecap="round"
              strokeDasharray={s.dashed ? '6 4' : undefined}
              points={s.points.map(p => `${geo.x(p.t)},${geo.y(p.value)}`).join(' ')} />
          )}
          {s.points.map((p, i) => (
            <circle key={i} cx={geo.x(p.t)} cy={geo.y(p.value)} r={p.flagged ? 5 : (i === s.points.length - 1 ? 4.5 : 3.2)}
              fill={s.color} stroke={p.flagged ? 'var(--red)' : 'none'} strokeWidth={p.flagged ? 2 : 0}>
              <title>{p.label}</title>
            </circle>
          ))}
        </g>
      ))}
    </svg>
  );
}
